import { Input } from "antd";
import { AntDModal } from "../../../components/antDmodal";
import { FaSearch } from "react-icons/fa";
import { useMemo, useState } from "react";
import {
  STOCK_EXCHANGES,
  getExchangeInfo,
  marketLogoMap,
} from "../../../helper";
import QuestionIcon from "../../../icons/questions.svg";

interface StockSearchModalProps {
  isModalOpen: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  filteredStocks: any[];
  handleSelectStock: (stock: any) => void;
  setIsModalOpen: (value: boolean) => void;
  callCompareFunction: Function;
  loading?: boolean;
  clearSearchOnSelect?: boolean;
}

const sortStocks = (left: any, right: any) =>
  `${left?.company_name || ""} ${left?.ticker_symbol || ""}`.localeCompare(
    `${right?.company_name || ""} ${right?.ticker_symbol || ""}`,
  );

export const StockSearchModal: React.FC<StockSearchModalProps> = ({
  isModalOpen,
  searchQuery,
  setSearchQuery,
  filteredStocks,
  handleSelectStock,
  setIsModalOpen,
  callCompareFunction,
  loading = false,
  clearSearchOnSelect = true,
}) => {
  const [selectedStock, setSelectedStock] = useState<null | any>(null);
  const [selectedExchange, setSelectedExchange] = useState<string>("all");

  const stockExchanges = useMemo(() => {
    const exchanges = new Set<string>();
    filteredStocks?.forEach((stock) => {
      if (stock.market_name) {
        exchanges.add(stock.market_name);
      }
    });

    const discovered = Array.from(exchanges);
    return [
      ...STOCK_EXCHANGES.filter((exchange) => exchanges.has(exchange)),
      ...discovered
        .filter((exchange) => !STOCK_EXCHANGES.includes(exchange))
        .sort(),
    ];
  }, [filteredStocks]);

  const displayedStocks = useMemo(() => {
    const normalizedStocks = Array.isArray(filteredStocks)
      ? filteredStocks
      : [];
    if (selectedExchange === "all") {
      return normalizedStocks;
    }
    return normalizedStocks.filter(
      (stock) => stock.market_name === selectedExchange,
    );
  }, [filteredStocks, selectedExchange]);

  // Sorted alphabetically, same as the previous grouped view did within
  // each group — just applied across the whole flat list now that the
  // per-exchange grouping (and its card-style section headers) is gone,
  // to match AiScreen's plain Ticker/Company/Market table exactly.
  const sortedStocks = useMemo(
    () => [...displayedStocks].sort(sortStocks),
    [displayedStocks],
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedStock(null);
    setSearchQuery("");
    setSelectedExchange("all");
  };

  return (
    <AntDModal
      title="Search for Stocks"
      open={isModalOpen}
      onCancel={handleModalClose}
      okText="Confirm"
      footer={false}
      loading={loading}
      // AiScreen's equivalent modal doesn't set a width at all, so it
      // falls back to antd's own default (~520px) — this one explicitly
      // set 920px, which is why it rendered noticeably larger. Dropped
      // to match.
      wrapClassName="vi-cmp-modal"
    >
      {/* flex column, capped to the modal's own max-height (set in
          compare.theme.css) — subtitle/search/chips stay a fixed size
          and only the stock table below grows/scrolls, same as
          AiScreen's version (which never needs to, since it's short
          enough to always fit). */}
      <div className="flex h-full min-h-0 flex-col">
        <h1 className="flex-shrink-0 text-sm font-normal text-[#667085]">
          Browse stocks by exchange or search by ticker and company name.
        </h1>
        <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4">
          <div className="relative flex-shrink-0">
            <FaSearch className="absolute left-3 top-3.5 z-[999] text-[#667085]" />
            <Input
              placeholder="Search by ticker, company, or exchange"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 rounded-md placeholder:text-[#667085]"
            />
          </div>

          <div className="flex flex-shrink-0 gap-2 flex-wrap">
            <button
              onClick={() => setSelectedExchange("all")}
              className={`
                px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                ${
                  selectedExchange === "all"
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }
              `}
            >
              All Exchanges
            </button>
            {stockExchanges.map((exchange) => {
              const { logo, short } = getExchangeInfo(exchange);
              return (
                <button
                  key={exchange}
                  onClick={() => setSelectedExchange(exchange)}
                  className={`
                    px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                    flex items-center gap-2
                    ${
                      selectedExchange === exchange
                        ? "bg-blue-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }
                  `}
                >
                  {logo && (
                    <img
                      src={logo}
                      alt={short}
                      className="w-5 h-5 object-cover"
                    />
                  )}
                  {short}
                </button>
              );
            })}
          </div>

          {/* Stock List — same flat Ticker/Company Name/Market table as
              AiScreen's stockSelectorModal.tsx (no per-exchange section
              headers or grouping), on request. */}
          <div className="flex min-h-0 flex-1 flex-col rounded-md border border-[#D0D5DD]">
            <div className="grid flex-shrink-0 grid-cols-3 gap-4 mb-2 px-2 bg-[#F9FAFB] rounded-md p-2">
              <div className="font-[500] text-black text-[16px]">Ticker</div>
              <div className="font-[500] text-black text-[16px]">
                Company Name
              </div>
              <div className="font-[500] text-black text-[16px] text-right pr-2">
                Market
              </div>
            </div>

            <div className="space-y-2 w-full min-h-0 flex-1 !overflow-auto">
              {sortedStocks.length > 0 ? (
                sortedStocks.map((stock) => (
                  <div
                    key={stock.id}
                    className={`${
                      stock?.id === selectedStock?.id ? "bg-gray-300" : ""
                    } grid grid-cols-3 gap-4 px-2 py-2 hover:bg-gray-100 rounded cursor-pointer`}
                    onClick={() => {
                      setSelectedStock(stock);
                      handleSelectStock(stock);
                      callCompareFunction();
                      if (clearSearchOnSelect) {
                        setSearchQuery("");
                      }
                    }}
                  >
                    <div className="flex items-center space-x-2 w-full">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center">
                        <img
                          src={`/${stock.company_logo}`}
                          alt={stock.company_logo}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <span>{stock.ticker_symbol}</span>
                    </div>
                    <div className="truncate">{stock.company_name}</div>
                    <div className="w-full flex justify-end pr-2 gap-x-2">
                      {stock.short_name}
                      <img
                        src={marketLogoMap[stock?.market_name] ?? ""}
                        alt=""
                        className="w-6 h-6 object-cover"
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-10">
                  <img
                    src={QuestionIcon}
                    width={48}
                    height={48}
                    alt="Not found"
                  />
                  <h1 className="font-inter text-[#101828] font-[500] text-[16px] mt-4">
                    Stock Not Found
                  </h1>
                  <p className="font-inter text-[#667085] font-normal text-[14px] text-center mt-1 mb-2 max-w-[420px]">
                    We were not able to find the stock you were looking for.
                    Please try another search term or switch the exchange.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AntDModal>
  );
};
